/**
 * ServiceTypeFormDialog - Criação/edição de Tipo de Serviço (Fase 3 OS)
 *
 * Leiaute padrão max-w-5xl (tela cheia no mobile). Validações repetidas no
 * servidor por upsert_service_type.
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useChecklistTemplatesList } from '@/hooks/useChecklistTemplates';
import {
  CHECKLIST_STAGES, STOCK_ITEM_TYPE_OPTIONS, STOCK_MOVEMENT_OPTIONS,
  type ServiceChecklistStage, type ServiceType, type ServiceTypeInput, useServiceTypes,
} from '@/hooks/useServiceTypes';

const EMPTY: ServiceTypeInput = {
  id: null, name: '', description: '', color: '#3B82F6', duration_minutes: 60, sort_order: 0,
  requires_vehicle: false, requires_address: false, default_category_id: null, default_phase_id: null,
  stock_movement: 'none', stock_item_types: [], stock_selection_required: false, is_active: true, checklist_templates: [], requires_seller: false,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceType: ServiceType | null;
}

export function ServiceTypeFormDialog({ open, onOpenChange, serviceType }: Props) {
  const [form, setForm] = useState<ServiceTypeInput>(EMPTY);
  const { upsert, isSaving } = useServiceTypes();
  const { categories } = useTaskCategories(true, undefined, true);
  const { phases } = useTaskPhases(form.default_category_id ?? undefined, undefined, true);
  const { data: templates = [] } = useChecklistTemplatesList({ status: 'active' });

  // Carrega os dados ao abrir (edição) ou limpa (criação)
  useEffect(() => {
    if (!open) return;
    setForm(serviceType ? { ...serviceType } : EMPTY);
  }, [open, serviceType]);

  const set = <K extends keyof ServiceTypeInput>(k: K, v: ServiceTypeInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const templateOptions = useMemo(() => templates.map((t) => ({ value: t.id, label: t.name, hint: t.type_name })), [templates]);

  const addTemplate = (stage: ServiceChecklistStage) =>
    set('checklist_templates', [...form.checklist_templates, { template_id: '', stage, sort_order: form.checklist_templates.filter((l) => l.stage === stage).length }]);

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error('Informe o nome do tipo de serviço');
    if (!form.duration_minutes || form.duration_minutes < 1) return toast.error('Informe a duração em minutos');
    if (form.stock_movement !== 'none' && form.stock_item_types.length === 0) return toast.error('Selecione ao menos um item de estoque aceito');
    if (form.checklist_templates.some((l) => !l.template_id)) return toast.error('Escolha o template em todas as linhas de checklist');
    try {
      await upsert(form);
      toast.success('Tipo de serviço salvo');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Erro ao salvar tipo de serviço');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 p-0 sm:h-auto sm:max-h-[85dvh] sm:max-w-5xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{form.id ? 'Editar Tipo de Serviço' : 'Novo Tipo de Serviço'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Dados básicos */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nome *</Label>
              <Input maxLength={120} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Nova Instalação" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Descrição</Label>
              <Textarea maxLength={1000} rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Duração (minutos) *</Label>
              <Input type="number" min={1} max={10080} value={form.duration_minutes} onChange={(e) => set('duration_minutes', Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label>Ordem de exibição</Label>
              <Input type="number" min={0} value={form.sort_order} onChange={(e) => set('sort_order', Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label>Cor de identificação</Label>
              <div className="flex gap-2">
                <Input type="color" className="w-14 p-1" value={form.color} onChange={(e) => set('color', e.target.value.toUpperCase())} />
                <Input maxLength={7} value={form.color} onChange={(e) => set('color', e.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="flex items-end gap-6 pb-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.requires_vehicle} onCheckedChange={(v) => set('requires_vehicle', v)} />Exige veículo</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.requires_address} onCheckedChange={(v) => set('requires_address', v)} />Exige endereço</label>
              {/* BR-OS-020: exige vendedor na OS */}
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.requires_seller} onCheckedChange={(v) => set('requires_seller', v)} />Exige vendedor</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />Ativo</label>
            </div>
          </div>

          {/* Categoria/fase padrão para novas OS */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Categoria padrão</Label>
              <SearchableSelect value={form.default_category_id} noneLabel="Nenhuma"
                options={categories.map((c: any) => ({ value: c.id, label: c.name }))}
                selectedLabel={serviceType?.default_category_name}
                onChange={(v) => setForm((f) => ({ ...f, default_category_id: v, default_phase_id: null }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Fase padrão</Label>
              <SearchableSelect value={form.default_phase_id} noneLabel="Nenhuma" disabled={!form.default_category_id}
                options={phases.map((p: any) => ({ value: p.id, label: p.name }))}
                selectedLabel={serviceType?.default_phase_name}
                onChange={(v) => set('default_phase_id', v)} />
            </div>
          </div>

          {/* Estoque na conclusão */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1.5">
              <Label>Movimentação de estoque</Label>
              <Select value={form.stock_movement} onValueChange={(v: any) => setForm((f) => ({ ...f, stock_movement: v, ...(v === 'none' ? { stock_item_types: [], stock_selection_required: false } : {}) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STOCK_MOVEMENT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label} — {o.hint}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.stock_movement !== 'none' && (
              <div className="flex flex-wrap items-center gap-5">
                <span className="text-sm text-muted-foreground">Itens aceitos:</span>
                {STOCK_ITEM_TYPE_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.stock_item_types.includes(o.value)}
                      onCheckedChange={(c) => set('stock_item_types', c ? [...form.stock_item_types, o.value] : form.stock_item_types.filter((x) => x !== o.value))} />
                    {o.label}
                  </label>
                ))}
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.stock_selection_required} onCheckedChange={(v) => set('stock_selection_required', v)} />
                  Obrigatório selecionar para concluir
                </label>
              </div>
            )}
          </div>

          {/* Templates de checklist por etapa */}
          <div className="space-y-3">
            <div>
              <Label>Templates de Checklist</Label>
              <p className="text-xs text-muted-foreground">Os checklists serão criados em sequência: início → execução → conclusão.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {CHECKLIST_STAGES.map((stage) => (
                <div key={stage.value} className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{stage.label}</span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => addTemplate(stage.value)}><Plus className="mr-1 h-4 w-4" />Adicionar</Button>
                  </div>
                  {form.checklist_templates.map((l, idx) => l.stage !== stage.value ? null : (
                    <div key={idx} className="flex items-center gap-2">
                      <SearchableSelect className="flex-1" value={l.template_id || null} options={templateOptions} placeholder="Template..."
                        selectedLabel={l.template_name}
                        onChange={(v) => set('checklist_templates', form.checklist_templates.map((x, i) => i === idx ? { ...x, template_id: v ?? '' } : x))} />
                      <Button type="button" size="icon" variant="ghost" aria-label="Remover template"
                        onClick={() => set('checklist_templates', form.checklist_templates.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
